import { Patient } from 'fhir/r4b';
import { DateTime } from 'luxon';
import {
  PageSizes,
  PDFDocument,
  PDFFont,
  popGraphicsState,
  pushGraphicsState,
  RotationTypes,
  scale,
  StandardFonts,
  translate,
} from 'pdf-lib';
import { MIME_TYPES } from './file';
import { getImageOrientation, getOrientationCorrection } from './image-orientation';

export async function drawCardsPDF(
  cardFrontBytes: ArrayBuffer | undefined,
  cardFrontFiletype: string | undefined,
  cardBackBytes: ArrayBuffer | undefined,
  cardBackFiletype: string | undefined,
  patient: Patient
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('drawCardsPDF promise rejected. Timed out after 2.5s.'));
    }, 2500);

    const wrappedDraw = async (): Promise<void> => {
      try {
        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage();
        const [pageWidth, pageHeight] = PageSizes.A4;
        page.setSize(pageWidth, pageHeight);

        const xMargin = 63;
        const yMargin = 69;
        const spacing = 14;
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const [maxCardWidth, maxCardHeight] = [pageWidth - xMargin * 2, (pageHeight - yMargin * 2 - spacing * 3) / 2];

        const textStyles = {
          x: xMargin,
          y: pageHeight - yMargin,
          size: 14,
          font: helveticaFont,
        };

        const drawImage = async (
          imageBytes: ArrayBuffer,
          fileType: string | undefined,
          yOffset: number
        ): Promise<number> => {
          let image;
          switch (fileType) {
            case 'png':
              image = await pdfDoc.embedPng(imageBytes);
              break;
            case 'jpg':
            case 'jpeg':
              image = await pdfDoc.embedJpg(imageBytes);
              break;
            default:
              throw new Error(`Unsupported file type ${fileType}. File type must be one of: "png", "jpg", "jpeg"`);
          }

          const exifOrientation = getImageOrientation(imageBytes);
          const orientationCorrection = getOrientationCorrection(exifOrientation);

          let scaledImage, correctedWidth, correctedHeight;
          switch (exifOrientation) {
            case 5:
            case 6:
            case 7:
            case 8:
              // The uploaded image is rotated +/- 90 degrees
              scaledImage = image.scaleToFit(maxCardHeight, maxCardWidth);
              correctedWidth = scaledImage.height;
              correctedHeight = scaledImage.width;
              break;
            default:
              scaledImage = image.scaleToFit(maxCardWidth, maxCardHeight);
              correctedWidth = scaledImage.width;
              correctedHeight = scaledImage.height;
          }

          // Correct mirrored images
          // https://github.com/Hopding/pdf-lib/issues/183#issuecomment-569138703
          if (orientationCorrection.mirrored === 'x') {
            // scale - flips the image horizontally but keeps it in the same quadrant
            // translate - moves the coordinate system origin to the bottom right and flips the direction of the x-axis
            page.pushOperators(pushGraphicsState(), scale(-1, 1), translate(-pageWidth, 0));
          } else if (orientationCorrection.mirrored === 'y') {
            // scale - flips the image vertically but keeps it in the same quadrant
            // translate - moves the coordinate system origin to the top left and flips the direction of the y-axis
            page.pushOperators(pushGraphicsState(), scale(1, -1), translate(0, -pageHeight));
          }

          // Get x, y offset to shift corrected image
          let xShift, yShift;
          switch (exifOrientation) {
            case 2:
              xShift = pageWidth - xMargin - correctedWidth;
              yShift = yOffset - correctedHeight;
              break;
            case 3:
              xShift = xMargin + correctedWidth;
              yShift = yOffset;
              break;
            case 4:
              xShift = pageWidth - xMargin;
              yShift = yOffset;
              break;
            case 5:
              xShift = xMargin + correctedWidth;
              yShift = pageHeight - yOffset;
              break;
            case 6:
              xShift = xMargin;
              yShift = yOffset;
              break;
            case 7:
              xShift = xMargin;
              yShift = pageHeight - yOffset + correctedHeight;
              break;
            case 8:
              xShift = xMargin + correctedWidth;
              yShift = yOffset - correctedHeight;
              break;
            default:
              xShift = xMargin;
              yShift = yOffset - correctedHeight;
          }

          page.drawImage(image, {
            x: xShift,
            y: yShift,
            height: scaledImage.height,
            width: scaledImage.width,
            rotate: { angle: orientationCorrection.degrees, type: RotationTypes.Degrees },
          });

          if (orientationCorrection.mirrored) {
            page.pushOperators(popGraphicsState());
          }

          return correctedHeight;
        };

        // Draw the PDF
        page.drawText(
          `Patient: ${patient.name?.[0].given?.[0] || ''} ${patient.name?.[0].family || ''}, DOB: ${DateTime.fromISO(
            patient?.birthDate || ''
          ).toLocaleString(DateTime.DATE_SHORT)}`,
          textStyles
        );

        let cardFrontHeight;
        if (cardFrontBytes) {
          cardFrontHeight = await drawImage(cardFrontBytes, cardFrontFiletype, textStyles.y - spacing * 2);
        }

        if (cardBackBytes) {
          const yOffset =
            cardFrontBytes && cardFrontHeight
              ? pageHeight - cardFrontHeight - yMargin - spacing * 4
              : textStyles.y - spacing * 2;
          await drawImage(cardBackBytes, cardBackFiletype, yOffset);
        }

        // Save PDF
        const pdfBytes = await pdfDoc.save();
        // fs.writeFileSync(`./cards-temp.pdf`, pdfBytes);
        return resolve(pdfBytes);
      } catch (err) {
        console.log('Error drawing cards pdf: ', err, JSON.stringify(err));
        return reject(err);
      } finally {
        clearTimeout(timer);
      }
    };

    wrappedDraw().catch((err) => {
      throw new Error(err);
    });
  });
}

export async function uploadPDF(
  pdfBytes: Uint8Array,
  fileURL: string,
  token: string,
  patientId?: string
): Promise<void> {
  if (!patientId) {
    throw new Error('patient ID is undefined!');
  }

  console.log('getting presigned url for z3 upload');
  const presignedURLRequest = await fetch(fileURL, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action: 'upload' }),
  });
  console.log('got presigned url for z3 upload');

  console.log('getting presigned url json');
  const presignedURLResponse = await presignedURLRequest.json();
  if (!presignedURLRequest.ok) {
    console.log(presignedURLResponse);
    throw new Error(`Failed to get presigned url: ${presignedURLRequest.statusText}`);
  }
  console.log('got z3 url json', JSON.stringify(presignedURLResponse));

  console.log('uploading to z3 using presigned url');
  const uploadRequest = await fetch(presignedURLResponse.signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': MIME_TYPES.PDF,
    },
    body: pdfBytes,
  });

  if (!uploadRequest.ok) {
    throw new Error(`Upload request was not OK: ${uploadRequest.statusText}`);
  }
  console.log('upload to z3 using presigned url succeeded');
}

export interface FitWrappedTextToBannerResult {
  fontSize: number;
  lines: string[];
  lineHeight: number;
  ascender: number;
  blockHeight: number;
}

function wrapTextToFont(text: string, font: PDFFont, fontSize: number, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (font.widthOfTextAtSize(testLine, fontSize) > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [''];
}

function clampLinesWithEllipsis(
  lines: string[],
  font: PDFFont,
  fontSize: number,
  maxWidth: number,
  maxLines: number
): string[] {
  if (lines.length <= maxLines) return lines;

  const clamped = lines.slice(0, maxLines);
  const ellipsis = '...';
  let lastLine = clamped[maxLines - 1].trimEnd();

  while (lastLine.length > 0 && font.widthOfTextAtSize(`${lastLine}${ellipsis}`, fontSize) > maxWidth) {
    lastLine = lastLine.slice(0, -1).trimEnd();
  }

  clamped[maxLines - 1] = lastLine ? `${lastLine}${ellipsis}` : ellipsis;
  return clamped;
}

export function fitWrappedTextToBanner({
  text,
  font,
  maxWidth,
  initialFontSize,
  minFontSize,
  lineHeightRatio,
  bannerHeight,
  verticalPadding,
  maxLines,
}: {
  text: string;
  font: PDFFont;
  maxWidth: number;
  initialFontSize: number;
  minFontSize: number;
  lineHeightRatio: number;
  bannerHeight: number;
  verticalPadding: number;
  maxLines: number;
}): FitWrappedTextToBannerResult {
  for (let fontSize = initialFontSize; fontSize >= minFontSize; fontSize -= 1) {
    const lines = wrapTextToFont(text, font, fontSize, maxWidth);
    if (lines.length > maxLines) continue;

    const lineHeight = fontSize * lineHeightRatio;
    const ascender = font.heightAtSize(fontSize, { descender: false });
    const blockHeight = ascender + (lines.length - 1) * lineHeight;

    if (blockHeight <= bannerHeight - verticalPadding * 2) {
      return { fontSize, lines, lineHeight, ascender, blockHeight };
    }
  }

  const fontSize = minFontSize;
  const lines = clampLinesWithEllipsis(
    wrapTextToFont(text, font, fontSize, maxWidth),
    font,
    fontSize,
    maxWidth,
    maxLines
  );
  const lineHeight = fontSize * lineHeightRatio;
  const ascender = font.heightAtSize(fontSize, { descender: false });
  const blockHeight = ascender + (lines.length - 1) * lineHeight;

  return { fontSize, lines, lineHeight, ascender, blockHeight };
}
